import { useState } from 'react';
import { ApiError } from '../api/client';
import { useDiscordNotificationSettings, useUpdateDiscordNotificationSetting, useWorldOptions } from '../api/queries';
import type { DiscordNotificationSetting, DiscordNotificationType, User } from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';

const TYPE_LABELS: Record<DiscordNotificationType, string> = {
  sd_request_created: 'Neue SD-Anfrage (an Stammesführer)',
  sd_request_approved: 'Genehmigte SD-Anfrage (an Mitglieder)',
  retime_request_created: 'Neue Retime-Anfrage (an Mitglieder)',
  ag_request_created: 'Neue AG-Abfrage (an Mitglieder)',
  world_conquest: 'Eroberungen',
};

const TYPE_HINTS: Record<DiscordNotificationType, string> = {
  sd_request_created: 'Wird verschickt, sobald ein Mitglied eine neue SD-Anfrage stellt -- für den Kanal der Stammesführer.',
  sd_request_approved: 'Wird verschickt, sobald eine SD-Anfrage genehmigt wird -- für den Kanal, in dem die Mitglieder sind.',
  retime_request_created:
    'Wird verschickt, sobald ein Mitglied eine neue Retime-Anfrage stellt -- kein Genehmigungsschritt, sofort für den Kanal der Mitglieder.',
  ag_request_created:
    'Wird verschickt, sobald ein Stammesführer eine neue AG-Abfrage startet -- mit Link, unter dem die Mitglieder ihre AGs angeben. Die bisherigen Angaben werden dabei zurückgesetzt.',
  world_conquest:
    'Kündigt alle paar Minuten neue Adelungen dieser Welt an -- eigener Kanal empfohlen. Beim Aktivieren werden die letzten 24 Stunden nachgeholt.',
};

/**
 * Discord-Einstellungen: nur für Stammesführer und Administratoren
 * (serverseitig über `requireTribeLeaderOrAdmin` durchgesetzt), analog zur
 * Verwaltung-Seite. Eine gemeinsame Liste je Welt.
 */
export function DiscordSettingsPage({ user }: { user: User }) {
  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const settings = useDiscordNotificationSettings(activeWorld || undefined);
  const rows = settings.data?.settings ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Discord-Benachrichtigungen</h1>
          <p className="muted">
            Für jede Welt separat ein-/ausschaltbar, mit eigener Webhook-URL je Benachrichtigungs-Typ
            (unterschiedliche Kanäle möglich).
          </p>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && (
        <p className="muted center pad">Wähle eine Welt, um ihre Discord-Einstellungen zu sehen.</p>
      )}

      {activeWorld && (
        <>
          {settings.isPending && <Spinner label="Einstellungen werden geladen …" />}

          {settings.isError && (
            <div className="flash flash-error">
              {settings.error instanceof ApiError ? settings.error.message : 'Einstellungen konnten nicht geladen werden.'}
            </div>
          )}

          {rows.map((setting) => (
            <DiscordSettingRow key={setting.type} setting={setting} worldKey={activeWorld} />
          ))}
        </>
      )}
    </section>
  );
}

function DiscordSettingRow({ setting, worldKey }: { setting: DiscordNotificationSetting; worldKey: string }) {
  const [webhookUrl, setWebhookUrl] = useState('');
  const update = useUpdateDiscordNotificationSetting(worldKey);
  const toast = useToast();

  const toggle = () => {
    update.mutate(
      { type: setting.type, enabled: !setting.enabled, webhookUrl: webhookUrl.trim() || null },
      {
        onSuccess: () => toast.push('success', `"${TYPE_LABELS[setting.type]}" ist jetzt ${!setting.enabled ? 'aktiv' : 'aus'}.`),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const saveWebhook = () => {
    if (!webhookUrl.trim()) return;
    update.mutate(
      { type: setting.type, enabled: setting.enabled, webhookUrl: webhookUrl.trim() },
      {
        onSuccess: () => {
          toast.push('success', 'Webhook-URL wurde gespeichert.');
          setWebhookUrl('');
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const removeWebhook = () => {
    if (!window.confirm('Hinterlegte Webhook-URL entfernen?')) return;
    update.mutate(
      { type: setting.type, enabled: setting.enabled, webhookUrl: null },
      {
        onSuccess: () => toast.push('success', 'Webhook-URL wurde entfernt.'),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <div className="card" style={{ marginBottom: '12px' }}>
      <div className="card-head">
        <div>
          <h3 style={{ margin: 0 }}>{TYPE_LABELS[setting.type]}</h3>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {TYPE_HINTS[setting.type]}
          </p>
        </div>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
          <input type="checkbox" checked={setting.enabled} disabled={update.isPending} onChange={toggle} />
          <span>Aktiv</span>
        </label>
      </div>

      <div className="field-row" style={{ marginTop: '8px' }}>
        <label className="field grow">
          <span>Webhook-URL {setting.hasWebhookUrl && <span className="badge badge-active">hinterlegt</span>}</span>
          <input
            type="text"
            value={webhookUrl}
            onChange={(event) => setWebhookUrl(event.target.value)}
            placeholder={setting.hasWebhookUrl ? '•••• (neu eingeben zum Ersetzen)' : 'https://discord.com/api/webhooks/…'}
          />
        </label>
        <button type="button" className="btn btn-primary btn-sm" disabled={!webhookUrl.trim() || update.isPending} onClick={saveWebhook}>
          Speichern
        </button>
        {setting.hasWebhookUrl && (
          <button type="button" className="btn btn-danger btn-sm" disabled={update.isPending} onClick={removeWebhook}>
            Entfernen
          </button>
        )}
      </div>
      {setting.updatedByName && (
        <p className="muted" style={{ margin: '6px 0 0', fontSize: '0.85em' }}>
          Zuletzt geändert von {setting.updatedByName} am {new Date(setting.updatedAt!).toLocaleString('de-DE')}.
        </p>
      )}
    </div>
  );
}
